/// Home / level-select screen.
///
/// A pan/zoom world map (Pixi-rendered, pixi-viewport for transforms)
/// sits as the full-bleed background. For each level in the registry
/// we plot a pin in *world* space; on every viewport transform we
/// re-project those pins to screen coords and update DOM positions.
///
/// Clicking a pin opens a small modal with description + Play; Play
/// hands the level id back to the parent which switches screens.

import { useEffect, useRef, useState } from 'react'
import { userBadgeLabel } from '../auth/auth'
import { SignInButton, SignedInMenu } from './AuthControls'
import { AuthModal } from './AuthModal'
import { FeedbackModal } from './FeedbackModal'
import { HelpModal } from './HelpModal'
import { LeaderboardPanel } from './LeaderboardPanel'
import { SettingsModal } from './SettingsModal'
import { SigninNudgeModal } from './SigninNudgeModal'
import { WelcomeModal, hasSeenWelcome } from './WelcomeModal'
import { WhatsNewModal } from './WhatsNewModal'
import { hasSeenLatestWhatsNew, markLatestWhatsNewSeen } from '../data/whatsNew'
import {
  deleteInboxItem,
  markInboxItemRead,
  subscribeUnreadInbox,
  type InboxItem,
} from '../data/inbox'
import { shouldShowSigninNudge } from '../data/anonymousNudge'
import { LEVEL_REGISTRY, type LevelRegistryEntry } from '../data/loadLevel'
import { FULL_UNLOCK } from '../data/packs'
import { isLevelUnlocked, useUnlocks } from '../data/purchases'
import {
  logPurchaseButtonTapped,
  logPurchaseScreenViewed,
} from '../analytics/AnalyticsManager'
import {
  DIFFICULTY_ORDER,
  highestCompletedDifficulty,
  type DifficultyName,
  type LevelStats,
} from '../data/userProfile'
import { WorldMapScene } from '../render/WorldMapScene'
import { useProfileStore } from '../state/profileStore'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions } from '../firebase/app'

/// Zoom factor (multiple of fit-to-world) at which pin labels
/// switch on. Below this we show only dots; above, names appear.
/// Tuned empirically: 1× shows the whole world (cluttered);
/// 2× already separates NJ from Boston comfortably.
const LABEL_ZOOM_THRESHOLD = 2

/// Per-level remembered difficulty pick. Survives page reloads so a
/// player who's grinding Master on a level doesn't have to re-select
/// the tier every time.
export function difficultyKey(levelId: string): string {
  return `flatout.web.difficulty.${levelId}`
}

function readStoredDifficulty(levelId: string): DifficultyName | null {
  const raw = localStorage.getItem(difficultyKey(levelId))
  if (raw && DIFFICULTY_ORDER.includes(raw as DifficultyName)) {
    return raw as DifficultyName
  }
  return null
}

/// Pick a sensible default for the difficulty selector when the
/// modal opens. Priority:
///   1. Last-chosen value (persisted in localStorage)
///   2. The next tier above the highest-cleared difficulty
///   3. Trainee — the iOS app's default and a friendly starting point
function defaultDifficulty(
  levelId: string,
  stats: LevelStats | undefined,
): DifficultyName {
  const stored = readStoredDifficulty(levelId)
  if (stored) return stored
  const cleared = highestCompletedDifficulty(stats)
  if (cleared) {
    const idx = DIFFICULTY_ORDER.indexOf(cleared)
    const next = DIFFICULTY_ORDER[idx + 1]
    if (next) return next
    return cleared // already at Master — stay there
  }
  return 'Trainee'
}

export function HomeScreen(props: {
  onPlay: (levelId: string, difficulty: DifficultyName) => void
  onOpenDevBlog: (postId?: string) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<WorldMapScene | null>(null)
  /// Pin world-space positions are stable; only screen positions
  /// change on pan/zoom. Both are computed up-front and refreshed.
  const [pins, setPins] = useState<Pin[]>([])
  const [selected, setSelected] = useState<LevelRegistryEntry | null>(null)
  /// First-visit welcome modal — shown once per device, gated by
  /// localStorage. The "Get started" or Skip button dismisses
  /// permanently.
  const [welcomeOpen, setWelcomeOpen] = useState<boolean>(() => !hasSeenWelcome())
  /// What's New modal. `null` = closed, 'auto' = opened on mount
  /// because a new release id was detected, 'manual' = opened via
  /// the header `✨` button. First-time visitors are gated by the
  /// welcome modal: when they dismiss it we mark the current
  /// release seen too, so they don't get two modals in a row. The
  /// *next* release after their first visit then auto-opens
  /// normally.
  const [whatsNewMode, setWhatsNewMode] = useState<'auto' | 'manual' | null>(
    () => (hasSeenWelcome() && !hasSeenLatestWhatsNew() ? 'auto' : null),
  )
  const [signinNudgeOpen, setSigninNudgeOpen] = useState<boolean>(false)
  /// Help modal opened via the `?` button in the home-screen
  /// chrome. Only the Concepts tab — no level brief context here.
  const [helpOpen, setHelpOpen] = useState<boolean>(false)
  const [feedbackOpen, setFeedbackOpen] = useState<boolean>(false)
  /// Phase 2: unread developer-reply inbox. Subscribed live so the
  /// badge updates the moment the dev posts a reply (or the user
  /// reads it on another tab).
  const [unreadInbox, setUnreadInbox] = useState<InboxItem[]>([])
  /// Per-session "I've already seen the banner" toggle so it
  /// doesn't re-appear every time the user nav's back home from a
  /// post detail. Cleared when they actually mark something read
  /// or close the tab.
  const [inboxBannerDismissed, setInboxBannerDismissed] = useState(false)
  /// Settings modal opened via the gear button. Surfaces "show
  /// welcome again" and "replay TUTORIAL guide".
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false)
  /// Current viewport zoom as a multiple of fit-to-world. Drives
  /// label visibility — at low zoom we only show dots so the world
  /// view doesn't drown in overlapping NJ/Europe labels; once the
  /// player zooms into a region, labels appear.
  const [zoomFactor, setZoomFactor] = useState(1)
  const profileState = useProfileStore((s) => s.state)
  const playCount = useProfileStore((s) => s.playCount)
  const playingNow = useProfileStore((s) => s.playingNow)
  const playingNowCount = playingNow?.total ?? null
  /// Top 3 levels with active players, shown inline next to the
  /// total. Sorted by count desc, then by display name for stable
  /// ordering when counts tie. Falls back to "" silently if the
  /// breakdown hasn't loaded yet or no-one is in a level.
  const topLevelBreakdown = (() => {
    if (!playingNow) return [] as Array<{ id: string; name: string; count: number }>
    const idToName = new Map(
      LEVEL_REGISTRY.map((e) => [e.id, e.displayName] as const),
    )
    return Object.entries(playingNow.byLevel)
      .map(([id, count]) => ({ id, name: idToName.get(id) ?? id, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
      .slice(0, 3)
  })()
  const user = profileState.kind === 'ready' ? profileState.user : null
  const profile = profileState.kind === 'ready' ? profileState.profile : null
  const unlocks = useUnlocks()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let scene: WorldMapScene | null = null
    let cancelled = false

    const init = async () => {
      const s = await WorldMapScene.mount(container)
      if (cancelled) {
        s.destroy()
        return
      }
      scene = s
      sceneRef.current = s

      const initialPins: Pin[] = LEVEL_REGISTRY.map((entry) => {
        const world = s.worldFor(entry.pin.longitude, entry.pin.latitude)
        const screen = s.toScreen(world)
        return { entry, world, screen }
      })
      setPins(initialPins)

      const refresh = () => {
        setPins((prev) =>
          prev.map((p) => ({ ...p, screen: s.toScreen(p.world) })),
        )
        setZoomFactor(s.zoomFactor())
      }
      const unsubscribe = s.onTransform(refresh)
      // Also refresh once after init in case fit happened post-mount.
      refresh()
      ;(s as unknown as { __unsub?: () => void }).__unsub = unsubscribe

      // Zoom to the user's most-recent level on entry. First-time
      // visitors (no profile yet) and anyone whose stored
      // lastViewedLevel doesn't match a known id (e.g., a level we
      // removed) get pushed to TUTORIAL — better than dropping them
      // into a full-world fit and making them hunt for a starting
      // point.
      const last = profile?.lastViewedLevel
      const lastEntry =
        (last && LEVEL_REGISTRY.find((e) => e.id === last)) ||
        LEVEL_REGISTRY.find((e) => e.id === 'TUTORIAL') ||
        LEVEL_REGISTRY[0]
      if (lastEntry) {
        s.focusOn({
          lon: lastEntry.pin.longitude,
          lat: lastEntry.pin.latitude,
        })
      }
    }
    void init()

    return () => {
      cancelled = true
      const s = sceneRef.current
      sceneRef.current = null
      if (scene) {
        const unsub = (scene as unknown as { __unsub?: () => void }).__unsub
        if (unsub) unsub()
        scene.destroy()
        scene = null
      } else if (s) {
        s.destroy()
      }
    }
  }, [])

  /// Phase 2: subscribe to the user's unread inbox the moment auth
  /// resolves. Re-subscribes on a UID change (e.g., anonymous →
  /// signed-in transition via linkWithPopup keeps the UID, but
  /// signing out → fresh anonymous changes it).
  useEffect(() => {
    if (!user) return
    const unsub = subscribeUnreadInbox({
      uid: user.uid,
      onChange: setUnreadInbox,
      onError: () => setUnreadInbox([]),
    })
    return unsub
  }, [user?.uid])

  // Soft-trial sign-in nudge: once the welcome flow's out of the
  // way and the player has racked up enough finished runs as a
  // guest, surface the one-time nudge. Re-evaluates on every
  // user-state transition so a returning anonymous visitor with
  // an existing run-counter sees it on their next home-screen
  // mount.
  useEffect(() => {
    if (welcomeOpen) return
    if (!user) return
    if (shouldShowSigninNudge(user)) setSigninNudgeOpen(true)
  }, [user, welcomeOpen])

  const latestUnread = unreadInbox[0]
  const inboxBannerVisible = !!latestUnread && !inboxBannerDismissed

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="absolute inset-0" />
      {inboxBannerVisible && latestUnread && (
        <InboxBanner
          item={latestUnread}
          extraCount={Math.max(0, unreadInbox.length - 1)}
          onOpen={() => {
            // Don't mark-read here — DevBlogPostDetail handles that
            // when the thread mounts. Just close the banner and
            // navigate to the thread.
            setInboxBannerDismissed(true)
            props.onOpenDevBlog(latestUnread.postId)
          }}
          onDismiss={() => {
            if (!user) return
            setInboxBannerDismissed(true)
            void markInboxItemRead({ uid: user.uid, notifId: latestUnread.id }).catch(() => {})
          }}
          onDelete={() => {
            if (!user) return
            setInboxBannerDismissed(true)
            void deleteInboxItem({ uid: user.uid, notifId: latestUnread.id }).catch(() => {})
          }}
        />
      )}
      <header className="pointer-events-none absolute left-4 top-3 z-10 flex select-none flex-col gap-1 text-sm font-semibold tracking-wide text-white/80">
        <div className="flex items-center gap-3">
        <span>FlatoutATC <span className="text-white/40">— Web</span></span>
        <span
          className="pointer-events-auto rounded bg-slate-900/70 px-2 py-0.5 text-[11px] font-medium text-white/80 ring-1 ring-white/10"
          title={
            user?.isAnonymous
              ? 'Guest progress is saved on this browser only. Sign in (Google or email) to keep it across devices.'
              : undefined
          }
        >
          {userBadgeLabel(user)}
          {user?.isAnonymous && (
            <span
              className="ml-1 text-amber-300/90"
              aria-label="Guest progress is browser-local"
            >
              ⚠
            </span>
          )}
          {profile?.lastViewedLevel ? (
            <span className="ml-2 font-normal text-white/50">
              · last: {profile.lastViewedLevel}
            </span>
          ) : null}
        </span>
        {profile && (
          <span className="rounded bg-emerald-900/40 px-2 py-0.5 text-[11px] font-medium text-emerald-100 ring-1 ring-emerald-500/30">
            Total{' '}
            <span className="ml-1 font-mono">
              {(profile.totalScore ?? 0).toLocaleString()}
            </span>
          </span>
        )}
        </div>
        {(playCount !== null || playingNowCount !== null) && (
          <div className="flex items-center gap-3 text-[11px] font-normal text-white/50">
            {playCount !== null && (
              <span>
                Played{' '}
                <span className="font-mono text-white/70">
                  {playCount.toLocaleString()}
                </span>{' '}
                times
              </span>
            )}
            {playingNowCount !== null && playingNowCount > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                <span className="font-mono text-white/70">
                  {playingNowCount.toLocaleString()}
                </span>{' '}
                playing now
                {topLevelBreakdown.length > 0 && (
                  <>
                    {' — '}
                    {topLevelBreakdown.map((b, i) => (
                      <span key={b.id}>
                        {i > 0 && <span className="text-white/30"> · </span>}
                        <span className="font-mono text-white/70">{b.count}</span>{' '}
                        <span>in {b.name}</span>
                      </span>
                    ))}
                  </>
                )}
              </span>
            )}
          </div>
        )}
      </header>
      <div className="pointer-events-auto absolute right-4 top-3 z-10 flex select-none items-center gap-2">
        {user?.isAnonymous ? <SignInButton /> : user ? <SignedInMenu user={user} /> : null}
        <button
          onClick={() => sceneRef.current?.fitWorld()}
          title="Zoom out to fit world"
          className="h-7 rounded-md bg-slate-900/70 px-2.5 text-xs font-semibold text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80"
        >
          ⌖ World
        </button>
        <div className="relative">
          <button
            onClick={() => props.onOpenDevBlog()}
            title={
              unreadInbox.length > 0
                ? `Talk to the developer — ${unreadInbox.length} unread repl${unreadInbox.length === 1 ? 'y' : 'ies'}`
                : 'Talk to the developer'
            }
            aria-label="Talk to the developer"
            className="h-7 w-7 rounded-full bg-slate-900/70 text-sm text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80"
          >
            💬
          </button>
          {unreadInbox.length > 0 && (
            <span
              aria-hidden
              className="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white ring-1 ring-slate-900"
            >
              {unreadInbox.length > 9 ? '9+' : unreadInbox.length}
            </span>
          )}
        </div>
        <button
          onClick={() => setFeedbackOpen(true)}
          title="Send feedback"
          className="h-7 rounded-md bg-slate-900/70 px-2.5 text-xs font-semibold text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80"
        >
          Feedback
        </button>
        <button
          onClick={() => setWhatsNewMode('manual')}
          title="What's new"
          aria-label="What's new"
          className="h-7 w-7 rounded-full bg-slate-900/70 text-sm text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80"
        >
          ✨
        </button>
        <button
          onClick={() => setHelpOpen(true)}
          title="Help & concepts"
          className="h-7 w-7 rounded-full bg-slate-900/70 text-xs font-bold text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80"
        >
          ?
        </button>
        <button
          onClick={() => setSettingsOpen(true)}
          title="Settings"
          className="h-7 w-7 rounded-full bg-slate-900/70 text-sm text-white/80 shadow ring-1 ring-white/10 hover:bg-slate-800/80"
          aria-label="Settings"
        >
          ⚙
        </button>
        <span className="hidden rounded bg-slate-900/60 px-2 py-1 text-xs text-white/70 ring-1 ring-white/10 md:inline">
          Drag to pan · scroll/pinch to zoom
        </span>
      </div>

      {pins.map((p, i) => {
        // Even-indexed pins put their label to the right of the dot;
        // odd-indexed pins drop the label below. Cheap declutter when
        // two airports sit close enough for labels to collide.
        const labelClass =
          i % 2 === 0
            ? 'left-4 top-1/2 -translate-y-1/2'
            : 'left-1/2 top-4 -translate-x-1/2'
        const stats = profile?.levelStats?.[p.entry.id]
        const status = pinStatus(stats)
        const palette = pinPalette(status)
        // Labels appear once the player zooms past LABEL_ZOOM_THRESHOLD
        // (~the regional view). Below that the world view is just
        // dots so dense regions like NJ + central Europe don't drown
        // in overlapping text. The user's current last-played pin and
        // any selected pin are exempt — those always show their label
        // so the player can find their bearings.
        const isLast = profile?.lastViewedLevel === p.entry.id
        const isSelected = selected?.id === p.entry.id
        const showLabel = zoomFactor >= LABEL_ZOOM_THRESHOLD || isLast || isSelected
        const locked = !isLevelUnlocked(
          { isFree: p.entry.isFree ?? true },
          unlocks,
        )
        const liveCount = playingNow?.byLevel[p.entry.id] ?? 0
        return (
          <button
            key={p.entry.id}
            onClick={() => setSelected(p.entry)}
            style={{ left: p.screen.x, top: p.screen.y }}
            className="pointer-events-auto absolute z-10 -translate-x-1/2 -translate-y-1/2 transform"
            title={
              (locked
                ? `${p.entry.displayName} — Locked (Unlock $${FULL_UNLOCK.defaultPriceUSD.toFixed(2)})`
                : `${p.entry.displayName} — ${pinStatusLabel(status)}`) +
              (liveCount > 0
                ? ` · ${liveCount} playing now`
                : '')
            }
          >
            <span className="relative flex h-3 w-3 items-center justify-center">
              {/* Locked levels show a 🔒 instead of the colored dot.
                  Unlocked-but-unattempted pins ping; engaged pins go
                  static so the map doesn't churn with competing
                  animations. */}
              {locked ? (
                <span
                  className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-900/80 text-[10px] leading-none ring-1 ring-amber-300/70"
                  aria-label="Locked"
                >
                  🔒
                </span>
              ) : (
                <>
                  {status === 'unplayed' && (
                    <span
                      className={`absolute inline-flex h-full w-full animate-ping rounded-full ${palette.fill} opacity-60`}
                    ></span>
                  )}
                  <span
                    className={`relative inline-flex h-3 w-3 rounded-full ${palette.fill} ring-2 ${palette.ring}`}
                  ></span>
                </>
              )}
            </span>
            {liveCount > 0 && (
              // Live-presence badge — small emerald pill anchored to
              // the pin's upper-right. Pulses softly so a busy map
              // reads as an at-a-glance heatmap of where players
              // are right now. Hidden when zero (the common case)
              // so the map stays calm during off-hours.
              <span
                className="pointer-events-none absolute -right-2 -top-2 inline-flex items-center gap-0.5 rounded-full bg-emerald-500/95 px-1 py-px font-mono text-[9px] font-bold leading-none text-white shadow ring-1 ring-white/30"
                aria-label={`${liveCount} playing now`}
              >
                <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-white/90" />
                {liveCount}
              </span>
            )}
            {showLabel && (
              <span
                className={
                  'absolute whitespace-nowrap rounded bg-slate-900/85 px-1.5 py-0.5 text-[11px] font-semibold text-white shadow ring-1 ring-white/10 ' +
                  labelClass
                }
              >
                {p.entry.displayName}
                {status !== 'unplayed' && (
                  <span className={`ml-1.5 ${palette.text}`}>{pinStatusBadge(status)}</span>
                )}
              </span>
            )}
          </button>
        )
      })}

      {selected && (
        <LevelDetailModal
          entry={selected}
          stats={profile?.levelStats?.[selected.id]}
          currentUid={user?.uid ?? null}
          onCancel={() => setSelected(null)}
          onPlay={(difficulty) => {
            localStorage.setItem(difficultyKey(selected.id), difficulty)
            props.onPlay(selected.id, difficulty)
          }}
        />
      )}
      {welcomeOpen && (
        <WelcomeModal
          onDismiss={() => {
            setWelcomeOpen(false)
            // First-time visitors: don't immediately fire What's New on
            // top of the welcome they just dismissed. Mark current
            // release seen so the next release triggers normally.
            markLatestWhatsNewSeen()
          }}
        />
      )}
      {whatsNewMode && (
        <WhatsNewModal
          trigger={whatsNewMode}
          onDismiss={() => setWhatsNewMode(null)}
        />
      )}
      {signinNudgeOpen && (
        <SigninNudgeModal onDismiss={() => setSigninNudgeOpen(false)} />
      )}
      {helpOpen && (
        <HelpModal
          brief={undefined}
          goals={null}
          initialTab="concepts"
          onDismiss={() => setHelpOpen(false)}
        />
      )}
      {feedbackOpen && (
        <FeedbackModal levelId={null} onDismiss={() => setFeedbackOpen(false)} />
      )}
      {settingsOpen && (
        <SettingsModal
          onDismiss={() => setSettingsOpen(false)}
          onShowWelcome={() => setWelcomeOpen(true)}
        />
      )}
    </div>
  )
}

/// Detail modal shown after a pin is clicked. Owns its own
/// difficulty-selection state so the parent doesn't have to keep a
/// per-level pick around — it's only read once on Play.
const createCheckoutSession = httpsCallable<
  { productId: string },
  { url: string; sessionId: string }
>(firebaseFunctions, 'createStripeCheckoutSession')

export function LevelDetailModal(props: {
  entry: LevelRegistryEntry
  stats: LevelStats | undefined
  currentUid: string | null
  onCancel: () => void
  onPlay: (difficulty: DifficultyName) => void
}) {
  const { entry, stats, currentUid, onCancel, onPlay } = props
  const [difficulty, setDifficulty] = useState<DifficultyName>(() =>
    defaultDifficulty(entry.id, stats),
  )
  const [showLeaderboard, setShowLeaderboard] = useState(false)
  const [buying, setBuying] = useState(false)
  const [buyError, setBuyError] = useState<string | null>(null)
  const [authForBuyOpen, setAuthForBuyOpen] = useState(false)
  const cleared = stats?.completed ?? {}
  const unlocks = useUnlocks()
  const profileState = useProfileStore((s) => s.state)
  const user = profileState.kind === 'ready' ? profileState.user : null
  const locked = !isLevelUnlocked(
    { isFree: entry.isFree ?? true },
    unlocks,
  )

  // Analytics: surface the paywall view exactly once per modal open
  // for a locked level. Cheap effect — re-fires on level change.
  useEffect(() => {
    if (locked) {
      logPurchaseScreenViewed({ levelId: entry.id })
    }
  }, [locked, entry.id])

  const startCheckout = async () => {
    setBuying(true)
    setBuyError(null)
    try {
      const result = await createCheckoutSession({
        productId: FULL_UNLOCK.stripeProductId,
      })
      if (result.data?.url) {
        window.location.href = result.data.url
      } else {
        throw new Error('Stripe returned no checkout URL')
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      console.warn('createCheckoutSession failed', e)
      setBuyError(`Couldn’t start checkout: ${message}`)
      setBuying(false)
    }
  }

  const handleBuy = async () => {
    logPurchaseButtonTapped({
      levelId: entry.id,
      productId: FULL_UNLOCK.stripeProductId,
    })
    if (!user || user.isAnonymous) {
      // Anonymous: route through the AuthModal first. On
      // successful sign-in we'll auto-resume the Stripe checkout
      // so the player only has to press Unlock once.
      setAuthForBuyOpen(true)
      return
    }
    await startCheckout()
  }
  return (
    <div
      className="pointer-events-auto absolute inset-0 z-20 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-sm rounded-xl bg-slate-900 p-6 text-slate-100 shadow-2xl ring-1 ring-white/10"
      >
        <h2 className="text-lg font-semibold tracking-wide">{entry.displayName}</h2>
        <p className="mt-2 text-sm text-slate-300">{entry.description}</p>
        <LevelStatsRow stats={stats} />
        <div className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Difficulty
          </div>
          <div className="flex gap-1.5">
            {DIFFICULTY_ORDER.map((d) => {
              const isSelected = d === difficulty
              const isCleared = cleared[d] != null
              return (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  className={
                    'flex-1 rounded-md px-2 py-2 text-xs font-semibold transition-colors ' +
                    (isSelected
                      ? 'bg-emerald-600 text-white shadow ring-1 ring-emerald-400'
                      : 'bg-slate-800 text-slate-300 ring-1 ring-white/10 hover:bg-slate-700 hover:text-white')
                  }
                  title={isCleared ? `Cleared at ${d}` : d}
                >
                  {d}
                  {isCleared && (
                    <span className="ml-1 text-emerald-300">✓</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
        {locked && (
          <div className="mt-4 rounded-lg bg-slate-800/70 px-3 py-3 ring-1 ring-amber-300/30">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-300/80">
              Locked
            </div>
            <div className="mt-1 text-sm font-semibold text-slate-100">
              {FULL_UNLOCK.displayName}
            </div>
            <p className="mt-1 text-xs text-slate-400">
              {FULL_UNLOCK.description}
            </p>
            <div className="mt-2 text-base font-mono font-semibold text-emerald-300">
              ${FULL_UNLOCK.defaultPriceUSD.toFixed(2)}
            </div>
          </div>
        )}
        {buyError && (
          <div className="mt-3 rounded-md bg-rose-900/50 px-3 py-2 text-xs text-rose-100 ring-1 ring-rose-500/30">
            {buyError}
          </div>
        )}
        <div className="mt-5 flex justify-between gap-2">
          <button
            onClick={() => setShowLeaderboard(true)}
            className="rounded-md bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-200 ring-1 ring-white/10 hover:bg-slate-700 hover:text-white"
            title="View top scores for this level"
          >
            🏆 Leaderboard
          </button>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={buying}
              className="rounded-md px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-white/5 hover:text-white"
            >
              Cancel
            </button>
            {locked ? (
              <button
                onClick={handleBuy}
                disabled={buying}
                className={
                  'rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow ' +
                  (buying ? 'opacity-60' : 'hover:bg-emerald-500')
                }
              >
                {buying ? 'Opening checkout…' : `Unlock $${FULL_UNLOCK.defaultPriceUSD.toFixed(2)}`}
              </button>
            ) : (
              <button
                onClick={() => onPlay(difficulty)}
                className="rounded-md bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500"
              >
                Play
              </button>
            )}
          </div>
        </div>
      </div>
      {showLeaderboard && (
        <LeaderboardPanel
          levelId={entry.id}
          levelDisplayName={entry.displayName}
          initialDifficulty={difficulty}
          currentUid={currentUid}
          onClose={() => setShowLeaderboard(false)}
        />
      )}
      {authForBuyOpen && (
        <AuthModal
          eyebrow="Sign in to unlock"
          title="Save your purchase across devices"
          onSignedIn={() => {
            // Continue with the Stripe checkout the moment the
            // sign-in lands. Wrapped in a microtask so the modal
            // unmount finishes before the redirect kicks in.
            window.setTimeout(() => void startCheckout(), 0)
          }}
          onDismiss={() => setAuthForBuyOpen(false)}
        />
      )}
    </div>
  )
}

interface Pin {
  entry: LevelRegistryEntry
  world: { x: number; y: number }
  screen: { x: number; y: number }
}

/// Six pin states reflecting per-level progression.
type PinStatus = 'unplayed' | 'attempted' | DifficultyName

function pinStatus(stats: LevelStats | undefined): PinStatus {
  if (!stats) return 'unplayed'
  const completed = highestCompletedDifficulty(stats)
  if (completed) return completed
  return 'attempted'
}

interface PinPalette {
  fill: string
  ring: string
  text: string
}

/// Color palette by status. Tailwind class fragments rather than raw
/// hex so the palette tracks the radar theme via the build's
/// `safelist` (Tailwind's JIT preserves these).
function pinPalette(status: PinStatus): PinPalette {
  switch (status) {
    case 'unplayed':
      return { fill: 'bg-emerald-400', ring: 'ring-emerald-200/80', text: 'text-emerald-200' }
    case 'attempted':
      return { fill: 'bg-amber-400', ring: 'ring-amber-200/80', text: 'text-amber-200' }
    case 'Novice':
      return { fill: 'bg-emerald-500', ring: 'ring-emerald-300', text: 'text-emerald-200' }
    case 'Trainee':
      return { fill: 'bg-sky-500', ring: 'ring-sky-300', text: 'text-sky-200' }
    case 'Licensed':
      return { fill: 'bg-violet-500', ring: 'ring-violet-300', text: 'text-violet-200' }
    case 'Master':
      return { fill: 'bg-yellow-400', ring: 'ring-yellow-200', text: 'text-yellow-200' }
  }
}

function pinStatusLabel(status: PinStatus): string {
  switch (status) {
    case 'unplayed':
      return 'not yet attempted'
    case 'attempted':
      return 'attempted, not completed'
    default:
      return `completed at ${status}`
  }
}

function pinStatusBadge(status: PinStatus): string {
  switch (status) {
    case 'unplayed':
      return ''
    case 'attempted':
      return '·'
    case 'Novice':
      return '✓N'
    case 'Trainee':
      return '✓T'
    case 'Licensed':
      return '✓L'
    case 'Master':
      return '✓M'
  }
}

/// Per-level stats line shown inside the pin-detail modal: best
/// score, best success%, play count, and which difficulty (if any)
/// the player has cleared. Hidden for unplayed levels.
function LevelStatsRow(props: { stats: LevelStats | undefined }) {
  const s = props.stats
  if (!s || s.playCount === 0) return null
  const best = highestCompletedDifficulty(s)
  return (
    <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-md bg-slate-800/60 px-3 py-2 text-xs">
      <dt className="text-slate-400">Best score</dt>
      <dd className="text-right font-mono text-slate-100">
        {s.bestScore.toLocaleString()}
      </dd>
      <dt className="text-slate-400">Best success</dt>
      <dd className="text-right font-mono text-slate-100">
        {Math.round(s.bestSuccessPct * 100)}%
      </dd>
      <dt className="text-slate-400">Plays</dt>
      <dd className="text-right font-mono text-slate-100">{s.playCount}</dd>
      {best && (
        <>
          <dt className="text-slate-400">Cleared</dt>
          <dd className="text-right font-mono text-emerald-300">{best}</dd>
        </>
      )}
    </dl>
  )
}

/// Top-of-screen banner shown when the user has at least one
/// unread developer-reply notification. Three actions:
///   - Open  → navigates to the post detail (marks read on mount)
///   - Dismiss → marks just this entry read, no nav
///   - ×     → hard-deletes the entry (useful for stale items
///             pointing at deleted posts)
/// The banner only renders for the *most recent* unread entry;
/// the `+N more` chip nudges the user to open the dev-blog page
/// where they can read the rest from the badge.
function InboxBanner(props: {
  item: InboxItem
  extraCount: number
  onOpen: () => void
  onDismiss: () => void
  onDelete: () => void
}) {
  const { item, extraCount } = props
  const lead =
    item.postKind === 'feedback'
      ? 'The developer replied to your feedback'
      : 'The developer replied to your post'
  return (
    <div
      role="status"
      className="pointer-events-auto absolute left-1/2 top-3 z-20 w-[min(640px,calc(100%-1rem))] -translate-x-1/2 rounded-lg bg-emerald-950/90 px-3 py-2 text-xs text-emerald-50 shadow-lg ring-1 ring-emerald-400/30 backdrop-blur-sm"
    >
      <div className="flex items-start gap-2">
        <span aria-hidden className="text-base leading-tight">💬</span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold">
            {lead}
            {item.postTitle ? <span className="text-emerald-200/80"> — {item.postTitle}</span> : null}
            {extraCount > 0 && (
              <span className="ml-2 rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-medium text-emerald-200 ring-1 ring-emerald-400/30">
                +{extraCount} more
              </span>
            )}
          </div>
          {item.preview && (
            <p className="mt-0.5 line-clamp-2 break-words text-emerald-100/80">{item.preview}</p>
          )}
          <div className="mt-1.5 flex items-center gap-3 text-[11px] font-medium">
            <button
              type="button"
              onClick={props.onOpen}
              className="rounded bg-emerald-500 px-2 py-0.5 font-semibold text-emerald-950 hover:bg-emerald-400"
            >
              Open
            </button>
            <button
              type="button"
              onClick={props.onDismiss}
              className="text-emerald-200 hover:text-white"
            >
              Mark read
            </button>
            <button
              type="button"
              onClick={props.onDelete}
              className="ml-auto text-emerald-300/70 hover:text-rose-300"
              title="Delete this notification"
              aria-label="Delete this notification"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
